import camelCase from "lodash/camelCase";
import uniqBy from "lodash/uniqBy";
import { ROOT_COLUMN_KEY } from "PFApp/booking/parts/overview/booking_module_calendar/booking_calendar/calendar_content/dual_scrollable_grid/constants";
import { useAvailableWithCustomTypesColumns } from "PFApp/components/table_properties/use_available_with_custom_types_columns";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { TableColumn } from "PFCore/types";
import { useTranslation } from "react-i18next";

const BOOKING_MODULE_PROJECTS_SECTION_KEY = "booking_module_projects";
const BLOCKED_PROPERTIES = ["availability"];

type UseRolesColumnsReturn = {
  availableColumns: TableColumn[];
};

export const useRolesColumns = (): UseRolesColumnsReturn => {
  const { t } = useTranslation("bookingModule", { keyPrefix: "rowsSidePanel.columns" });
  const roleTemplate = useTemplate(TemplateKey.Role);
  const auditRoleTemplate = useTemplate(TemplateKey.AuditRole);

  // Role columns and Audit Role columns merged together
  const commonColumns: TableColumn[] = [
    {
      name: ROOT_COLUMN_KEY,
      title: t("roleTitle"),
      isDefault: true,
      locked: true,
      fixedPosition: true
    },
    {
      name: "id",
      title: t("id")
    },
    {
      name: "status",
      title: t("status")
    },
    {
      name: "postUntil",
      title: t("expiryDate"),
      type: "date"
    },
    {
      name: "assignee",
      title: t("assignee")
    },
    {
      name: "due",
      title: t("dueInDays")
    },
    {
      name: "createdAt",
      title: t("dateCreated"),
      type: "date",
      orderKey: "activity.saas_created_at"
    },
    {
      name: "startDate",
      title: t("startDate"),
      type: "date",
      orderKey: "activity.start_date"
    },
    {
      name: "principalOwner",
      title: t("principalOwner")
    },
    {
      name: "engagement",
      title: t("engagement")
    },
    {
      name: "resource",
      title: t("resource")
    },
    {
      name: "sourceRole",
      title: t("sourceRole"),
      type: "rollforward_source"
    },
    {
      name: "targetRoles",
      title: t("targetRoles"),
      type: "rollforward_targets"
    }
  ];

  const { customTypesColumns: roleCustomTypesColumns } = useAvailableWithCustomTypesColumns({
    blockedProperties: BLOCKED_PROPERTIES,
    template: roleTemplate,
    availableColumns: [],
    sectionKey: BOOKING_MODULE_PROJECTS_SECTION_KEY
  });
  const { customTypesColumns: auditRoleCustomTypesColumns } = useAvailableWithCustomTypesColumns({
    blockedProperties: BLOCKED_PROPERTIES,
    template: auditRoleTemplate,
    availableColumns: [],
    sectionKey: BOOKING_MODULE_PROJECTS_SECTION_KEY
  });

  const availableColumns = uniqBy(
    [
      ...[...commonColumns, ...roleCustomTypesColumns, ...auditRoleCustomTypesColumns].map((column) => ({
        ...column,
        name: camelCase(column.name),
        type: camelCase(column.type) as typeof column.type
      }))
    ],
    "name"
  );

  return { availableColumns };
};
